import { ManualSectionType } from '@src/configs/manual.config'

/**
 * 웹 매뉴얼 검색 — 라이브러리 없이 절 단위 부분 문자열 매칭 (MANUAL_WEB_PLAN §4.4).
 * 절이 160여 개뿐이라 타이핑마다 전부 훑어도 1ms 단위다. 오타 허용이 필요해지면 그때 바꾼다.
 */

/**
 * 소문자 + 라틴 악센트 제거(é → e). 한·중·일은 그대로 둔다.
 * 글자 단위로 접어 **길이를 바꾸지 않는다** — 접힌 문자열의 위치가 원문 위치와 같아야
 * 스니펫을 원문에서 잘라 낼 수 있다.
 */
export const foldText = (text: string): string => {
	let out = ''
	for (const ch of text) {
		const folded = ch.normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC').toLowerCase()
		out += folded.length === ch.length ? folded : ch
	}
	return out
}

export const searchTermsOf = (query: string): string[] =>
	foldText(query.trim()).split(/\s+/).filter(Boolean)

export interface ManualSearchEntryType {
	section: ManualSectionType
	title: string
	body: string
}

export const buildSearchIndex = (
	sections: ManualSectionType[],
): ManualSearchEntryType[] =>
	sections.map(aSection => ({
		section: aSection,
		title: foldText(aSection.title),
		body: foldText(aSection.text),
	}))

export interface ManualSearchResultType {
	section: ManualSectionType
	score: number
	/** 일치 문장 한 줄 — hit 인 조각만 굵게 */
	snippet: { text: string; hit: boolean }[]
}

const countOf = (haystack: string, needle: string, cap: number): number => {
	let count = 0
	let from = haystack.indexOf(needle)
	while (from !== -1 && count < cap) {
		count += 1
		from = haystack.indexOf(needle, from + needle.length)
	}
	return count
}

/** 원문 한 구간을 검색어 기준으로 hit / 비hit 조각으로 나눈다 */
const markParts = (original: string, folded: string, terms: string[]) => {
	const parts: { text: string; hit: boolean }[] = []
	let cursor = 0
	while (cursor < folded.length) {
		let next = -1
		let length = 0
		terms.forEach(aTerm => {
			const at = folded.indexOf(aTerm, cursor)
			if (at !== -1 && (next === -1 || at < next)) {
				next = at
				length = aTerm.length
			}
		})
		if (next === -1) break
		if (next > cursor) parts.push({ text: original.slice(cursor, next), hit: false })
		parts.push({ text: original.slice(next, next + length), hit: true })
		cursor = next + length
	}
	if (cursor < original.length) parts.push({ text: original.slice(cursor), hit: false })
	return parts
}

const snippetOf = (entry: ManualSearchEntryType, terms: string[]) => {
	const original = entry.section.text
	const at = terms
		.map(aTerm => entry.body.indexOf(aTerm))
		.filter(anIndex => anIndex !== -1)
		.sort((a, b) => a - b)[0]
	if (at === undefined) {
		const head = original.slice(0, 120)
		return [{ text: head + (original.length > 120 ? '…' : ''), hit: false }]
	}
	const start = Math.max(0, at - 48)
	const end = Math.min(original.length, at + 96)
	const parts = markParts(original.slice(start, end), entry.body.slice(start, end), terms)
	if (start > 0) parts.unshift({ text: '…', hit: false })
	if (end < original.length) parts.push({ text: '…', hit: false })
	return parts
}

/**
 * 점수: 절 번호 일치(`9.4` → 9.4 와 그 아래) > 제목 일치 > 본문 일치 횟수.
 * 검색어가 여럿이면 전부 들어 있는 절만 남긴다.
 */
export const searchManual = (
	index: ManualSearchEntryType[],
	query: string,
	limit = 20,
): ManualSearchResultType[] => {
	const terms = searchTermsOf(query)
	if (terms.length === 0) return []

	const resultList: ManualSearchResultType[] = []
	index.forEach(anEntry => {
		let score = 0
		const isMatch = terms.every(aTerm => {
			const num = anEntry.section.num.toLowerCase()
			const numHit = num !== '' && (num === aTerm || num.startsWith(`${aTerm}.`))
			const titleHit = anEntry.title.includes(aTerm)
			const bodyCount = countOf(anEntry.body, aTerm, 10)
			if (!numHit && !titleHit && bodyCount === 0) return false
			score +=
				(numHit ? (num === aTerm ? 300 : 80) : 0) +
				(titleHit ? (anEntry.title.startsWith(aTerm) ? 140 : 100) : 0) +
				bodyCount
			return true
		})
		if (isMatch) {
			resultList.push({
				section: anEntry.section,
				score,
				snippet: snippetOf(anEntry, terms),
			})
		}
	})

	// 같은 점수는 문서 순서 그대로 (sort 는 안정 정렬)
	return resultList.sort((a, b) => b.score - a.score).slice(0, limit)
}

/**
 * 본문 안 첫 일치 부분을 `<mark>` 로 잠깐 감싼다. 결과를 눌러 그 절로 갔을 때 어디를 보라는지 알려 준다.
 * 절 본문은 dangerouslySetInnerHTML 이라 React 가 손대지 않는 DOM 이다 — 직접 감싸고 직접 푼다.
 */
export const flashTerms = (root: HTMLElement, terms: string[], ms = 3000) => {
	if (terms.length === 0) return
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
	let node = walker.nextNode() as Text | null
	while (node !== null) {
		const folded = foldText(node.data)
		const hit = terms
			.map(aTerm => ({ at: folded.indexOf(aTerm), length: aTerm.length }))
			.filter(aHit => aHit.at !== -1)
			.sort((a, b) => a.at - b.at)[0]
		if (hit !== undefined) {
			const target = node.splitText(hit.at)
			target.splitText(hit.length)
			const mark = document.createElement('mark')
			mark.className = 'mnFlash'
			target.parentNode?.replaceChild(mark, target)
			mark.appendChild(target)
			window.setTimeout(() => {
				const parent = mark.parentNode
				if (parent === null) return
				parent.replaceChild(target, mark)
				parent.normalize()
			}, ms)
			return
		}
		node = walker.nextNode() as Text | null
	}
}
